import React, { useEffect, useState } from 'react';
import { Checkbox, Input, message, Pagination, Popconfirm, Select } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMasterUserSelector } from '../../../../redux/selector';
import { getUrlPage, isValid } from 'utils/helper';
import { ROLE_DESCRIPTIONS, ROLE_NAME } from 'utils/data';
import { AppState } from 'interface/stateApp';
import toast, { NotifyType } from 'utils/toast';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { SystemUser } from 'interface/Personal';
import { getSysUsers, SYS_USER_PAGE_SIZE, toggleUserActive, updateUser } from 'service/userService';
import CreateOrEditUserModal from './CreateOrEditUserModal';
import processError from 'utils/processError';
import LoadingArea from 'component/common/LoadingArea';

const { Option } = Select;

const UserManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [sysUsers, setSysUsers] = useState<SystemUser[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(SYS_USER_PAGE_SIZE);
  const [sysUserSize, setSysUserSize] = useState(0);
  const [selectSysUser, setSelectSysUser] = useState<SystemUser | undefined>(undefined);
  const user = useMasterUserSelector();
  const [keyModal, setKeyModal] = useState<any>();

  const [stateApp, setStateApp] = useState<AppState>({
    showQnTicketForm: false,
    showReportForm: false,
    showCreateUserForm: false,
  });

  const navi = useNavigate();

  const handleSelectUser = (sysUser: SystemUser) => {
    setSelectSysUser(sysUser);
    openModal();
  };

  const openModal = (action?: string) => {
    if (action === 'CREATE') {
      setSelectSysUser(undefined);
    }
    setKeyModal(Date.now());
    setStateApp({ ...stateApp, showCreateUserForm: true });
  };
  const handleUpdateSysUsers = () => {
    handlePageChange(0);
  };

  const onClose = () => {
    setStateApp({ ...stateApp, showCreateUserForm: false });
  };

  const getData = async (url?: string) => {
    setLoading(true);
    getSysUsers(url)
      .then((response) => {
        setSysUsers(response?.content);
        setSysUserSize(response?.totalElements);
      })
      .finally(() => setLoading(false));
  };

  const handlePageChange = (page: number, pageSize?: number) => {
    setCurrentPage(page);
    setPageSize(pageSize ? pageSize : SYS_USER_PAGE_SIZE);
    const searchParam = searchParams.get('keyword');
    const roleParam = searchParams.get('role');

    let url = '/user/sys-users';
    if (isValid(roleParam)) {
      url += `?role=${roleParam}`;
      getData(getUrlPage(url, page, pageSize ? pageSize : SYS_USER_PAGE_SIZE)).then();
      return;
    }
    if (isValid(searchParam)) {
      url += `?keyword=${searchParam}`;
      getData(getUrlPage(url, page, pageSize ? pageSize : SYS_USER_PAGE_SIZE)).then();
      return;
    }
    getData(getUrlPage(url, page, pageSize ? pageSize : SYS_USER_PAGE_SIZE)).then();
  };

  const handleDelete = async (ticketId: any) => {
    setLoading(true);
    try {
      const response = await updateUser(ticketId, { id: ticketId, active: false });
      if (response !== null) {
        setSysUsers((prevTickets) => prevTickets.filter((ticket) => ticket.id !== ticketId));
        toast(NotifyType.SUCCESS, 'Xóa User thành công!');
      } else {
        toast(NotifyType.SUCCESS, 'Không thể xóa User này!');
      }
    } catch (err) {
      processError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    let input = event.currentTarget.value;
    if (event.key === 'Enter') {
      if (input !== '') {
        navi(`/user-management/user?keyword=${input}`);
      } else {
        navi(`/user-management/user`);
      }
    }
  };

  const handleOnchangeSelect = (value: string) => {
    if (value === 'all') {
      navi(`/user-management/user`);
    } else {
      navi(`/user-management/user?role=${value}`);
    }
  };

  const handleToggleActive = async (user: SystemUser) => {
    try {
      setLoading(true);
      await toggleUserActive(user.id);
      handlePageChange(currentPage);
      message.success(user.active ? `Disable user thành công` : `Active user thành công`);
    } catch (err) {
      processError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handlePageChange(0);
  }, [searchParams]);

  if (loading) {
    return <LoadingArea />;
  }

  return (
    <div className="container flex flex-col mx-auto p-4 h-[98%]">
      <CreateOrEditUserModal
        user={selectSysUser}
        stateApp={stateApp}
        onClose={onClose}
        key={keyModal}
        onData={handleUpdateSysUsers}
      ></CreateOrEditUserModal>
      <div className="p-2 flex items-center justify-between bg-white rounded">
        <div className="flex gap-4">
          <button
            className="py-1 bg-[#3498db] hover:bg-[#2980b9] flex items-center rounded px-2 text-white"
            onClick={() => openModal('CREATE')}
          >
            <PlusOutlined className="text-[15px]" />
            <span className="pl-2">Thêm mới</span>
          </button>
          <div className="min-w-[170px] max-w-[170px] flex items-center gap-2">
            <Select
              className="w-full"
              placeholder="Role - Tất cả"
              defaultValue={'all'}
              style={{ flex: 1 }}
              onChange={(value) => handleOnchangeSelect(value)}
            >
              <Option value="all">Role - Tất cả</Option>
              <Option value={ROLE_NAME.ROLE_CTV}>Công tác viên</Option>
              <Option value={ROLE_NAME.ROLE_EXPERT}>Chuyên gia</Option>
              <Option value={ROLE_NAME.ROLE_STUDENT}>Học sinh</Option>
              <Option value={ROLE_NAME.ROLE_COMPILER}>Biên tập</Option>
              <Option value={ROLE_NAME.ROLE_MASTER_COMPILER}>Tổng biên tập</Option>
            </Select>
          </div>
        </div>
        <Input placeholder="Nhập từ khóa tìm kiếm" name="keyword" className="w-fit" onKeyDown={handleKeyPress} />
      </div>
      <div className="flex flex-col bg-white h-[100%] p-2 rounded-sm">
        <div className="overflow-x-auto bg-white">
          <table className="min-w-full bg-white border border-gray-300">
            <thead className="bg-[#f2f2f2]">
              <tr>
                <th className="border border-gray-300 text-center py-3 px-4 font-semibold text-sm">Chọn</th>
                <th className="border border-gray-300 text-left py-3 px-4  font-semibold text-sm">Email</th>
                <th className="border border-gray-300 text-left py-3 px-4  font-semibold text-sm">First name</th>
                <th className="border border-gray-300 text-left py-3 px-4  font-semibold text-sm">Last name</th>
                <th className="border border-gray-300 text-center py-3 px-4  font-semibold text-sm">Role</th>
                <th className="border border-gray-300 text-center py-3 px-4  font-semibold text-sm">Active</th>
                <th className="border border-gray-300 text-center py-3 px-4  font-semibold text-sm">Hành động</th>
              </tr>
            </thead>
            <tbody className="text-gray-700">
              {sysUsers?.map((sysUser: SystemUser) => (
                <tr key={sysUser.id}>
                  <td className="border border-gray-300 text-center py-3 px-4 break-words">
                    <input type="checkbox" />
                  </td>
                  <td className="border border-gray-300 text-left py-3 px-4 break-words cursor-pointer">
                    {sysUser?.email}
                  </td>
                  <td className="border border-gray-300 text-left py-3 px-4 break-words cursor-pointer">
                    {sysUser?.firstName}
                  </td>
                  <td className="border border-gray-300 text-left py-3 px-4 break-words cursor-pointer">
                    {sysUser?.lastName}
                  </td>
                  <td className="border border-gray-300 text-center py-3 px-4 break-words">
                    {sysUser?.role ? ROLE_DESCRIPTIONS[sysUser?.role] : 'Unknown Role'}
                  </td>
                  <td className="border border-gray-300 text-center py-3 px-4 break-words cursor-pointer">
                    <Popconfirm
                      title={sysUser?.active ? 'Disable user' : 'Active user'}
                      description={`Xác nhận ${sysUser?.active ? 'disable user' : 'active user'}`}
                      onConfirm={() => handleToggleActive(sysUser)}
                      okText="Đồng ý"
                      cancelText="Hủy"
                    >
                      <Checkbox checked={sysUser?.active} />
                    </Popconfirm>
                  </td>

                  {user !== null && user?.role === ROLE_NAME.ROLE_ADMIN ? (
                    <td className="flex items-center justify-center border border-gray-300 text-center py-3 border-b-0">
                      <button
                        className="bg-blue-500 hover:bg-blue-700 text-white py-1 rounded-lg mx-1 w-[40px]"
                        onClick={() => handleSelectUser(sysUser)}
                      >
                        <EditOutlined />
                      </button>
                      <Popconfirm
                        title="Xóa người dùng"
                        description="Xác nhận xóa người dùng. Hành động này không thể hoàn tác?"
                        onConfirm={() => handleDelete(sysUser?.id)}
                        okText="Đồng ý"
                        cancelText="Hủy"
                      >
                        <button className="bg-red-500 hover:bg-red-700 text-white py-1 rounded-lg mx-1 w-[40px]">
                          <DeleteOutlined />
                        </button>
                      </Popconfirm>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* <div className="flex-grow"></div> */}
        <div className="flex justify-end mt-3 mb-2">
          <Pagination
            defaultCurrent={currentPage}
            total={sysUserSize}
            pageSize={pageSize}
            onChange={handlePageChange}
          />
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
